import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { describeManageError, fieldOfError } from '../manage/manage';
import {
  checkListForm,
  EMPTY_LIST_FORM,
  kindLabel,
  LIST_LIMITS,
  listCreateVariables,
  ListForm,
  listFormOf,
  listUpdateVariables,
  ManagedList,
} from './listManager';
import { CRM_MANAGER_CREATE_LIST, CRM_UPDATE_LIST } from './lists.queries';

interface ListEditorDialogProps {
  /** The list or saved filter to rename; absent for a new list. */
  list?: ManagedList;
  onClose: () => void;
  onSaved: (list: ManagedList, made: boolean) => void;
}

/** Makes an empty list, or renames and re-describes a list or saved filter. Nobody joins or leaves. */
export const ListEditorDialog = ({ list, onClose, onSaved }: ListEditorDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState<ListForm>(() => (list ? listFormOf(list) : EMPTY_LIST_FORM));
  const [tried, setTried] = useState(false);
  const [refusal, setRefusal] = useState<unknown>();
  const [create, created] = useMutation<{ crmCreateList: ManagedList }>(CRM_MANAGER_CREATE_LIST);
  const [update, updated] = useMutation<{ crmUpdateList: ManagedList }>(CRM_UPDATE_LIST);
  const running = created.loading || updated.loading;

  const problems = checkListForm(form);
  const refusedField = refusal === undefined ? undefined : fieldOfError(refusal);
  const refusedText = refusal === undefined ? undefined : describeManageError(refusal, 'list');
  const errorOf = (field: keyof ListForm) => (refusedField === field ? refusedText : tried ? problems[field] : undefined);

  const set = (field: keyof ListForm) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (refusedField === field) setRefusal(undefined);
  };

  const save = async () => {
    setTried(true);
    setRefusal(undefined);
    if (Object.keys(problems).length) return;
    try {
      if (list) {
        const changes = listUpdateVariables(list, form);
        if (!Object.keys(changes).length) {
          onClose();
          return;
        }
        const { data } = await update({ variables: { id: list.id, ...changes } });
        if (data) onSaved(data.crmUpdateList, false);
      } else {
        const { data } = await create({ variables: listCreateVariables(form) });
        if (data) onSaved(data.crmCreateList, true);
      }
    } catch (err) {
      setRefusal(err);
    }
  };

  const noun = list ? kindLabel(list).toLowerCase() : 'list';

  return (
    <Dialog
      open
      onClose={running ? undefined : onClose}
      fullScreen={phone}
      fullWidth
      maxWidth="sm"
      aria-labelledby="crm-list-editor-title"
    >
      <DialogTitle id="crm-list-editor-title">{list ? `Rename ${list.name}` : 'New list'}</DialogTitle>
      <Box
        component="form"
        noValidate
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          save();
        }}
      >
        <DialogContent dividers sx={{ display: 'grid', gap: 2.5 }}>
          {!list && (
            <Alert severity="info" variant="outlined">
              A list starts empty. To fill it, tick people on the contact list, or select everyone a filter matches, then
              choose <strong>Add to list</strong>.
            </Alert>
          )}
          <TextField
            label="Name"
            value={form.name}
            onChange={(event) => set('name')(event.target.value)}
            autoFocus
            required
            fullWidth
            disabled={running}
            error={Boolean(errorOf('name'))}
            helperText={errorOf('name') ?? 'Names need not be unique: NationBuilder allowed repeats, so some migrated lists share one.'}
            inputProps={{ maxLength: LIST_LIMITS.name + 20 }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(event) => set('description')(event.target.value)}
            disabled={running}
            multiline
            minRows={2}
            fullWidth
            error={Boolean(errorOf('description'))}
            helperText={
              errorOf('description') ??
              `Who is on this ${noun} and why. Optional. ${form.description.trim().length}/${LIST_LIMITS.description}`
            }
          />
          {refusal !== undefined && !refusedField && <Alert severity="error">{refusedText}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={running}
            startIcon={running ? <CircularProgress size={16} color="inherit" aria-label="Working" /> : undefined}
          >
            {list ? 'Save changes' : 'Create list'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
